<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="grid">
      <div
        id="product-gallery"
        class="main-product__gallery-wrapper col xs-span l7"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="tabs-vertical"
          :loading="state === 'loading'"
          :product="productObject"
          :section="section"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        >
          <template #countdown>
            <div v-if="showCountdown" class="main-product__countdown">
              <span
                class="main-product__countdown-label text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
                v-text="dailyDealObject[0].text"
              />
              <slot name="countdown-timer" />
            </div>
          </template>
        </product-gallery>
      </div>

      <div class="main-product__main col xs-span l5">
        <div class="main-product__buy-box">
          <div class="main-product__details">
            <h1
              id="template-title"
              class="title-mobile-m-bold title-desktop-m-bold main-product__title"
              v-text="
                productObject.customTitle
                  ? productObject.customTitle
                  : productObject.title
              "
            />
            <wishlist-button
              class="product-form__wishlist"
              :product="productObject"
              template="product"
              :variant="variant"
            />
            <product-badges
              class="main-product__badges"
              :loading="state === 'loading'"
              :product="productObject"
              template="product"
              :variant="variant"
              :bundle-disabled="bundleDisabled"
            />

            <star-rating-link
              class="main-product__reviews"
              :product="productObject"
            />
            <ul v-if="uspArray.length" class="main-product__usp">
              <li v-for="(usp, index) of uspArray" :key="index">
                <icon-check-outline />
                <span v-text="usp" />
              </li>
            </ul>
          </div>

          <product-form
            class="main-product__form"
            :loading="state === 'loading'"
            :product="productObject"
            :section="section"
            :variant="variant"
            :bundle="bundle"
            location="PDP"
            @update-variant="handleVariantUpdate"
            @update-bundle-status="(value) => (bundleDisabled = value)"
          />

          <product-info-overlay
            class="main-product__info-overlay"
            :product="productObject"
            :variant="variant"
            :lab-analysis-content="labAnalysis"
            :has-liquid-nutrition="hasLiquidNutrition"
          />
        </div>

        <div
          v-if="availableQuickUpsellProducts.length"
          class="main-product__quick-recommendations"
        >
          <h2
            class="main-product__quick-recommendations-title"
            v-text="$string('product.quick_upsell.title')"
          />
          <product-compact-carousel
            additional-classes="main-product__compact-carousel"
            quick-add-location="Quick Add To Cart - quick upsell"
            :product-list="availableQuickUpsellProducts"
            :product-count="availableQuickUpsellProducts.length"
          />
        </div>

        <slot name="product-usps" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} bundle - Canvas formatted bundle object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { mapActions, mapState } from 'pinia';
import useLastViewedProductStore from '~stores/last-viewed-products';
import useCartStore from '~stores/cart';

import IconCheckOutline from '~icons/general/check-outline.svg';
import ProductBadges from '~global/product-badges/product-badges';
import ProductForm from '~async/product-form/product-form';
import ProductGallery from '~async/product-gallery/product-gallery';
import StarRatingLink from '~global/star-rating/star-rating-link';
import WishlistButton from '~async/wishlist-button/wishlist-button';
import ProductCompactCarousel from '~async/product-compact-carousel/product-compact-carousel';

import productQuery from '~graphql/product.gql';
import productBundleQuery from '~graphql/product-queries/product-bundle.gql';
import { formatProduct, formatProductBundles } from '~/format/format-product';
import { formatTrackingItem } from '~/format/format-tracking-item';

import {
  computeDiscountLogic,
  emitUpdateRequestObject,
  isPreviewMode,
} from '~/helpers/vue';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import ProductInfoOverlay from '~async/product-info-overlay/product-info-overlay.vue';

export default {
  name: 'MainProduct',

  components: {
    IconCheckOutline,
    ProductBadges,
    ProductForm,
    ProductGallery,
    ProductInfoOverlay,
    StarRatingLink,
    WishlistButton,
    ProductCompactCarousel,
  },

  provide() {
    return {
      sectionId: this.section.id,
    };
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    bundle: {
      type: [Object],
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    designMode: {
      type: Boolean,
      default: false,
    },
    dailyDealObject: {
      type: Array,
      default: () => [],
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    labAnalysis: {
      type: Object,
      default: () => ({}),
    },
    hasLiquidNutrition: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      bundleDisabled: false,
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: this.$variable('page.handle'),
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),

    /**
     * Compute product object.
     * @returns {Object}
     */
    productObject() {
      if (this.state === 'loading' || !this.product) {
        return this.liquid;
      }

      // Use liquid data in preview mode or design mode when GraphQL product isn't available
      const inPreviewMode = isPreviewMode(this.designMode);

      if (inPreviewMode && !this.product.handle) {
        return this.liquid;
      }

      // Merge liquid data (usp) with GraphQL product data
      return {
        ...this.product,
        usp: this.liquid.usp,
      };
    },

    uspArray() {
      if (!this.productObject.usp || !Array.isArray(this.productObject.usp)) {
        return [];
      }

      return this.productObject.usp.filter(Boolean).slice(0, 3);
    },

    /**
     * Check if countdown should be shown (deal is active and not expired).
     * @returns {Boolean}
     */
    showCountdown() {
      if (!this.dailyDealObject?.[0]) return false;

      const dealData = this.dailyDealObject[0];

      // Check if daily deal is enabled
      if (!dealData.enabled) return false;

      // Check if end date is in the future
      let endDate;
      if (cnvs.store.country === 'GB') {
        // GMT needs to be adjusted +1 hour since Shopify uses CET
        const gmtDate = new Date(dealData.end_date_gmt);
        gmtDate.setHours(gmtDate.getHours() + 1);
        endDate = gmtDate;
      } else {
        endDate = new Date(dealData.end_date_cet || dealData.end_time);
      }

      const isActive = endDate && endDate > new Date();
      if (!isActive) return false;

      // Check if product is a daily deal
      const productGid = `gid://shopify/Product/${this.product.id}`;
      if (dealData.products?.includes(productGid)) {
        return true;
      }

      // Check if current variant is a daily deal
      const currentId =
        this.variant?.id || this.product.selectedOrFirstAvailableVariant?.id;
      const variantGid = `gid://shopify/ProductVariant/${currentId}`;
      return (
        dealData.variants?.some((variant) => variant === variantGid) || false
      );
    },

    /**
     * Filter quick upsell products to only include available products.
     * - Note: availableState does not exist at time of loading.
     * @returns {Array} - List of available quick upsell products.
     */
    availableQuickUpsellProducts() {
      if (!this.product.quickUpsell?.length) {
        return [];
      }

      return this.product.quickUpsell.filter(
        (product) => product.availableState ?? product.available,
      );
    },
  },

  watch: {
    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle);
    },

    /**
     * Watch product for update to load new product.
     * @param {Object} newValue - New product.
     */
    product(newValue) {
      if (!newValue?.id) {
        return;
      }

      this.addViewedProduct(newValue.id);
    },
  },

  mounted() {
    this.$emit('update-request-object', {
      response: this.liquid,
      type: 'product',
    });

    this.queryProduct();
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useLastViewedProductStore, ['addViewedProduct']),

    ...mapActions(useCartStore, {
      getCartAttributeCode: 'getCartAttributeCode',
    }),

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return;
      }

      this.state = 'loading';

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        });

        if (this.$variable('settings.disableComponentReadyState')) {
          return;
        }

        let formattedProduct = formatProduct(response);

        // If product type is 'bundle', fetch bundle metafield data
        if (formattedProduct.type === 'bundle') {
          try {
            const bundleResponse = await cnvs.Query({
              query: productBundleQuery,
              variables: {
                country: this.$variable('store.country'),
                handle,
                language: this.$variable('store.language'),
              },
            });

            // Merge bundle data into the formatted product
            if (bundleResponse?.product?.bundle) {
              formattedProduct = {
                ...formattedProduct,
                bundle: formatProductBundles(bundleResponse.product),
              };
            }
          } catch (bundleError) {
            cnvs.ReportError('Failed to load bundle data', bundleError);
          }
        }

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        });

        this.updateProductUrl(formattedProduct.handle);
        this.state = 'ready';
      } catch (error) {
        cnvs.ReportError('Failed to load product', error);
        // Set state to ready even on error so liquid fallback is used
        this.state = 'ready';
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0];

      if (!oldHandle || oldHandle === handle) {
        return;
      }

      const pathname = location.pathname.replace(oldHandle, handle);
      let url = `${location.origin}${pathname}`;

      /** for preview mode we need to have a different url structure */
      if (window.location.pathname.includes('products_preview')) {
        url = `${location.origin}${location.pathname}${location.search}`;
      }

      history.replaceState(
        {
          html: '',
        },
        '',
        url,
      );
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.currentIndex = newIndex;
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace;
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.handleUpdateCurrentIndex(0);

      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      });

      const params = new URLSearchParams(document.location.search.substring(1));

      if (variant?.id) {
        params.set('variant', variant.id);
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`;

      history.replaceState(
        {
          html: '',
        },
        '',
        url,
      );

      // Tracking event view item.
      this.handleTrackingViewItem(variant);

      // meta tag update
      this.updateMetaTags(variant);
    },

    /**
     * Tracking event view item.
     */
    handleTrackingViewItem(variant) {
      // Get available variant
      let selectedVariant = this.product.selectedOrFirstAvailableVariant;
      if (variant) {
        selectedVariant = variant;
      }
      const price = selectedVariant.price?.amount;
      const compareAtPrice = selectedVariant.compare_at_price?.amount;
      const discountLogic = computeDiscountLogic({
        discountPreview: false,
        onSale: compareAtPrice > price,
        product: this.product,
        variant: selectedVariant,
      });

      const campaignDiscount = this.getCampaignDiscount(
        discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };

      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');
      const viewItemValue = selectedVariant.price?.amount / 100;

      const item = formatTrackingItem({
        product: this.product,
        variant: selectedVariant,
        template: 'PDP',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: 1,
      });

      cnvs.EventBus.emit('dataLayer:view_item', {
        event: 'view_item',
        currency: currencyCode,
        value: viewItemValue,
        items: [item],
      });
    },

    /**
     * Update meta tags for social share with variant data.
     */
    updateMetaTags(variant) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogImageAlt = document.querySelector(
        'meta[property="og:image:alt"]',
      );
      const ogImageHeight = document.querySelector(
        'meta[property="og:image:height"]',
      );
      const ogImageWidth = document.querySelector(
        'meta[property="og:image:width"]',
      );
      const ogSecureUrl = document.querySelector(
        'meta[property="og:image:secure_url"]',
      );
      const twitterImage = document.querySelector('meta[name="twitter:image"]');

      if (ogImage) {
        ogImage.setAttribute('content', variant.featuredImage?.src);
      }

      if (ogImageAlt) {
        ogImageAlt.setAttribute('content', variant.featuredImage?.alt);
      }

      if (ogImageHeight) {
        ogImageHeight.setAttribute('content', variant.featuredImage?.height);
      }

      if (ogImageWidth) {
        ogImageWidth.setAttribute('content', variant.featuredImage?.width);
      }

      if (ogSecureUrl) {
        ogSecureUrl.setAttribute('content', window.location.href);
      }

      if (twitterImage) {
        twitterImage.setAttribute('content', variant.featuredImage?.src);
      }
    },
  },
};
</script>

<style lang="scss">
@import './main-product';
</style>
